// MedicationWizard.jsx
import { useState, useEffect, useRef } from 'react';

function MedicationWizard({
  csvData,
  label,
  description,
  commonCategories,
  onSelectionChange,
  initialSelections = [],
}) {
  const [data, setData] = useState([]);
  const [selCatId, setSelCatId] = useState(null);
  const [selClass, setSelClass] = useState(null);
  const [selGeneric, setSelGeneric] = useState(null);
  const [selections, setSelections] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const initialSyncedRef = useRef(false);
  const suppressOnChangeRef = useRef(false);
  const categoryRef = useRef(null);
  const classRef = useRef(null);
  const genericRef = useRef(null);
  const brandRef = useRef(null);
  const selectedRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load CSV Data
  useEffect(() => {
    if (!csvData) return;
    let processedData = [];

    if (Array.isArray(csvData)) processedData = csvData;
    else if (typeof csvData === 'object')
      processedData = Object.values(csvData).filter(
        (item) =>
          item &&
          typeof item === 'object' &&
          (item.id || item.label || item.Class)
      );

    processedData = processedData.map((item, idx) => {
      if (!item.id) item.id = `cat-${idx}`;
      if (!item.label) item.label = item.Category || `Category ${idx + 1}`;
      return item;
    });

    setData(processedData);
  }, [csvData]);

useEffect(() => {
  if (!initialSelections) return;
  
  // Check if selections have actually changed
  const selectionsChanged = JSON.stringify(selections) !== JSON.stringify(initialSelections);
  
  if (selectionsChanged) {
    suppressOnChangeRef.current = true;
    setSelections(initialSelections);
    
    // Restore UI state based on first selection
    if (initialSelections.length > 0) {
      const firstSel = initialSelections[0];
      if (data && data.length) {
        const foundCat = data.find(
          (c) =>
            c.label === firstSel.Category ||
            c.label === firstSel.category ||
            c.id === firstSel.category_id
        );
        if (foundCat) setSelCatId(foundCat.id);

        const foundClass = foundCat?.classes?.find(
          (cls) => cls.class === firstSel.Class || cls.class === firstSel.class
        );
        if (foundClass) setSelClass(foundClass.class);

        const foundGeneric = foundClass?.generics?.find(
          (g) => g.generic === firstSel.Generic || g.generic === firstSel.generic
        );
        if (foundGeneric) setSelGeneric(foundGeneric.generic);
      }
    }
    
    setTimeout(() => {
      suppressOnChangeRef.current = false;
    }, 0);
  }
}, [initialSelections, data]);

  // Notify parent when selections change
  useEffect(() => {
    if (suppressOnChangeRef.current) return;
    if (onSelectionChange) onSelectionChange(selections);
  }, [selections, onSelectionChange]);

  // Derived UI helpers
  const commonCats = data.filter(
    (cat) => commonCategories && commonCategories.includes(cat.id)
  );
  const otherCats = data.filter(
    (cat) => !commonCategories || !commonCategories.includes(cat.id)
  );

  const displayedCategories = showAllCategories
    ? [...commonCats, ...otherCats]
    : commonCats.length > 0
    ? commonCats
    : data;

  // Mobile limit
  const mobileCategoryLimit = 3;
  const limitedCategories =
    isMobile && !showAllCategories
      ? displayedCategories.slice(0, mobileCategoryLimit)
      : displayedCategories;

  const hasMoreCategories =
    otherCats.length > 0 && (commonCats.length > 0 || isMobile);

  const currentCategory = data.find((c) => c.id === selCatId) || {};
  const currentClassObj =
    currentCategory?.classes?.find((c) => c.class === selClass) || {};
  const currentGenericObj =
    currentClassObj?.generics?.find((g) => g.generic === selGeneric) || {};

  function removeChip(index) {
    setSelections((prev) => prev.filter((_, i) => i !== index));
  }

  // Selection handlers
  function onSelectCategory(catId) {
    setSelCatId(catId);
    setSelClass(null);
    setSelGeneric(null);
    setTimeout(() => {
      if (classRef.current)
        classRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  }

  function onSelectClass(cls) {
    setSelClass(cls);
    setSelGeneric(null);
    setTimeout(() => {
      if (genericRef.current)
        genericRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  }

  function onSelectGeneric(gen) {
    setSelGeneric(gen);
    const baseItem = {
      Category: currentCategory.label || '',
      Class: selClass || '',
      Generic: gen,
      Brand: '',
    };

    setSelections((prev) => {
      const exists = prev.some(
        (s) =>
          s.Category === baseItem.Category &&
          s.Class === baseItem.Class &&
          s.Generic === baseItem.Generic &&
          s.Brand === baseItem.Brand
      );
      if (exists) return prev;
      return [...prev, baseItem];
    });

  setTimeout(() => {
  window.scrollTo({
    top: document.body.scrollHeight,
    behavior: 'smooth',
  });
}, 100);

  }

function onToggleBrand(brand) {
  if (!selGeneric) return;

  const baseItem = {
    Category: currentCategory.label || '',
    Class: selClass,
    Generic: selGeneric,
  };
  const brandItem = { ...baseItem, Brand: brand };

  setSelections((prev) => {
    const brandExists = prev.some(
      (s) =>
        s.Category === brandItem.Category &&
        s.Class === brandItem.Class &&
        s.Generic === brandItem.Generic &&
        s.Brand === brandItem.Brand
    );

    if (brandExists) {
      const filtered = prev.filter(
        (s) =>
          !(
            s.Category === brandItem.Category &&
            s.Class === brandItem.Class &&
            s.Generic === brandItem.Generic &&
            s.Brand === brandItem.Brand
          )
      );

      const hasGenericOnly = filtered.some(
        (s) =>
          s.Category === baseItem.Category &&
          s.Class === baseItem.Class &&
          s.Generic === baseItem.Generic &&
          s.Brand === ''
      );

      const hasOtherBrands = filtered.some(
        (s) =>
          s.Category === baseItem.Category &&
          s.Class === baseItem.Class &&
          s.Generic === baseItem.Generic &&
          s.Brand !== ''
      );

      if (!hasGenericOnly && !hasOtherBrands) {
        filtered.push({ ...baseItem, Brand: '' });
      }

      return filtered;
    } else {
      const withoutGenericOnly = prev.filter(
        (s) =>
          !(
            s.Category === baseItem.Category &&
            s.Class === baseItem.Class &&
            s.Generic === baseItem.Generic &&
            s.Brand === ''
          )
      );
      return [...withoutGenericOnly, brandItem];
    }
  });

  // 🔽 Scroll to bottom smoothly
  setTimeout(() => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  }, 100);
}


  function isBrandSelected(brand) {
    return selections.some(
      (s) =>
        s.Category === currentCategory.label &&
        s.Class === selClass &&
        s.Generic === selGeneric &&
        s.Brand === brand
    );
  }

  function isGenericSelected(gen) {
    return selections.some(
      (s) =>
        s.Category === currentCategory.label &&
        s.Class === selClass &&
        s.Generic === gen
    );
  }

  function removeGenericAndBrands(gen) {
    setSelections((prev) =>
      prev.filter(
        (s) =>
          !(
            s.Generic === gen &&
            s.Class === selClass &&
            s.Category === currentCategory.label
          )
      )
    );
  }

  // --- Render ---
  return (
    <div style={{ margin: '10px', fontFamily: 'system-ui, sans-serif' }}>
      <div className='wizard-heading'>
        <h1>{label}</h1>
      </div>
      {!data.length && <p>{description}</p>}
      {data.length > 0 && (
        <>
          {/* Category */}
          <section ref={categoryRef}>
            <h2>1) Category</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {limitedCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => onSelectCategory(cat.id)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 14,
                    border: '1px solid #ccc',
                    backgroundColor: cat.id === selCatId ? '#111827' : '#fbfbfc',
                    color: cat.id === selCatId ? '#fff' : '#111',
                    cursor: 'pointer',
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            {hasMoreCategories && (
              <button
                onClick={() => setShowAllCategories(!showAllCategories)}
                style={{
                  marginTop: '12px',
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid #3b82f6',
                  backgroundColor: '#fff',
                  color: '#3b82f6',
                  cursor: 'pointer',
                }}
              >
                {showAllCategories ? 'Show Less' : 'Show More'}
              </button>
            )}
          </section>

          {/* Class */}
          {selCatId && (
            <section ref={classRef} style={{ marginTop: 20 }}>
              <h2>2) Class</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {currentCategory.classes?.map((cls) => (
                  <button
                    key={cls.class}
                    onClick={() => onSelectClass(cls.class)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 14,
                      border: '1px solid #111827',
                      backgroundColor: selClass === cls.class ? '#111827' : '#fbfbfc',
                      color: selClass === cls.class ? '#fff' : '#111',
                      cursor: 'pointer',
                    }}
                  >
                    {cls.class}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Generic */}
          {selClass && (
            <section ref={genericRef} style={{ marginTop: 20 }}>
              <h2>3) Generic</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {currentClassObj.generics?.map((g) => {
                  const selected = isGenericSelected(g.generic);
                  return (
                    <button
                      key={g.generic}
                      onClick={() => {
                        if (selected) removeGenericAndBrands(g.generic);
                        else onSelectGeneric(g.generic);
                      }}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 14,
                        border: '1px solid #bbf7d0',
                        backgroundColor: selected ? '#bbf7d0' : '#fbfbfc',
                        color: selected ? '#065f46' : '#111',
                        cursor: 'pointer',
                      }}
                    >
                      {g.generic}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Brand */}
          {selGeneric && (
            <section ref={brandRef} style={{ marginTop: 20 }}>
              <h2>4) Brand (optional)</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {currentGenericObj.brands?.map((br) => {
                  const selected = isBrandSelected(br);
                  return (
                    <button
                      key={br}
                      onClick={() => onToggleBrand(br)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 14,
                        border: '1px solid #065f46',
                        backgroundColor: selected ? '#065f46' : '#fbfbfc',
                        color: selected ? '#fff' : '#111',
                        cursor: 'pointer',
                      }}
                    >
                      {br}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Selected */}
          {selections.length > 0 && (
            <section ref={selectedRef} style={{ marginTop: 30 }}>
              <h2>Selected Medications</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {selections.map((s, idx) => {
                  const label = s.Brand
                    ? `${s.Category} → ${s.Class} → ${s.Generic} → ${s.Brand}`
                    : `${s.Category} → ${s.Class} → ${s.Generic}`;
                  return (
                    <span
                      key={`${s.Category}-${s.Class}-${s.Generic}-${s.Brand}-${idx}`}
                      style={{
                        backgroundColor: '#f2f3f5',
                        borderRadius: 10,
                        padding: '6px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '13px',
                      }}
                    >
                      {label}
                      <button
                        onClick={() => removeChip(idx)}
                        style={{
                          marginLeft: 6,
                          border: 'none',
                          cursor: 'pointer',
                          background: 'transparent',
                          fontSize: '18px',
                          color: '#666',
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default MedicationWizard;